import { EndRecurranceALL, EndRecurranceNONE, INotificationRecurrence } from '@communication-center/data-objects';
import { WMSelect } from '@walkme/wm-ui';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { components } from 'react-select';
import { extractButtons, extractXButtons } from '../../../../../helpers/visualDesign/extractVisualDesignDataProps';
import { useSelector } from '../../../../../infrastructure/store.interface';
import classes from './SelectReoccurrence.module.scss';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';

const keyPrefix = 'components.editNotification.editNotificationContent.sendStepDrawer.selectReoccurrence.endReocurrence';

const CustomGroup = (props: $TSFixMe) => <components.Group {...props} innerProps={{ 'data-testid': `group-${props.data.testId}` }} />;

export const EndReoccurrenceSelect = ({
    menuPortalTarget,
    reoccurrenceEndButtons = null,
    onChange,
    disabled = false,
    placeholder,
}: {
    menuPortalTarget?: null;
    reoccurrenceEndButtons?: INotificationRecurrence['endRecurrenceButtons'] | null;
    onChange?: (value: INotificationRecurrence['endRecurrenceButtons']) => void;
    disabled?: boolean;
    placeholder?: string;
}) => {
    const { t } = useTranslation('', { keyPrefix });
    const visualDesignData = useSelector(store => store.edit.notification?.visualDesignData)!;
    const [newRecurrenceButtons, setNewRecurrenceButtons] = useState(reoccurrenceEndButtons);

    const buttonsOptions = useMemo(() => {
        if (visualDesignData) {
            const buttons = extractButtons(visualDesignData).map(buttonAction => ({ value: buttonAction.id, label: buttonAction.label }));
            const XButton = extractXButtons(visualDesignData).map(buttonAction => ({ value: buttonAction.id, label: t('xButton') }))[0];
            return [...buttons, ...(XButton ? [XButton] : [])];
        }

        return [];
    }, [t, visualDesignData]);

    const isEndRecurrenceForAllButtons = useMemo(() => newRecurrenceButtons?.includes(EndRecurranceALL) || false, [newRecurrenceButtons]);
    const isNoEndRecurrenceButtons = useMemo(() => newRecurrenceButtons?.includes(EndRecurranceNONE) || false, [newRecurrenceButtons]);
    const selectedValues = useMemo(() => {
        return (
                isEndRecurrenceForAllButtons ? [{ value: EndRecurranceALL, label: t('allButtons') }] :
                isNoEndRecurrenceButtons ? [{ value: EndRecurranceNONE, label: t('none')  }]:
                []
            ).concat(
            buttonsOptions?.filter(option => newRecurrenceButtons?.includes(option?.value))
        );
    }, [isEndRecurrenceForAllButtons, t, isNoEndRecurrenceButtons,  buttonsOptions, newRecurrenceButtons]);

    const onChangeRef = useRef<any>(null);

    const onEndReoccurrenceSelect = useCallback(
        async (selectedItems: any) => {
            const buttonsValues = selectedItems.map((button: $TSFixMe) => button.value);
            const newAllButtonsValue = buttonsValues.some((item: $TSFixMe) => item === EndRecurranceALL);
            const newNoneButtonsValue = buttonsValues.some((item: $TSFixMe) => item === EndRecurranceNONE);
            let selectedButtons;
            if (!newAllButtonsValue && !newNoneButtonsValue) {
              selectedButtons = buttonsValues;
            } else if (newAllButtonsValue) {
              selectedButtons = buttonsValues.filter((item: $TSFixMe) => item !== EndRecurranceALL);
            } else if (newNoneButtonsValue) {
              selectedButtons =  buttonsValues.filter((item: $TSFixMe) => item !== EndRecurranceNONE);
            }

            let endRecurrenceButtons
            if (!isEndRecurrenceForAllButtons && newAllButtonsValue) {
                endRecurrenceButtons = EndRecurranceALL;
            } else if (!isNoEndRecurrenceButtons && newNoneButtonsValue) {
                endRecurrenceButtons = EndRecurranceNONE;
            } else {
                endRecurrenceButtons = selectedButtons?.length ? selectedButtons : null;
            }
            setNewRecurrenceButtons(endRecurrenceButtons);
            onChange?.(endRecurrenceButtons);
        },
        [isEndRecurrenceForAllButtons, isNoEndRecurrenceButtons, onChange]
    );

    useEffect(() => {
        onChangeRef.current?.(reoccurrenceEndButtons);
        setNewRecurrenceButtons(reoccurrenceEndButtons);
    }, [reoccurrenceEndButtons]);

    return (
        <div data-testid={'end-recurrence-option-select'}>
            <Controller
                rules={{
                    validate: value => {
                        return value?.length > 0;
                    }
                }}
                render={({ field: { onChange }, fieldState: { invalid } }) => {
                    onChangeRef.current = onChange;
                    return (
                        <WMSelect
                            menuPortalTarget={menuPortalTarget}
                            value={selectedValues}
                            isMulti={true}
                            groupValues
                            placeholder={placeholder || t('placeholder')}
                            isClearable={false}
                            onChange={onEndReoccurrenceSelect}
                            options={[
                                { options: [{ value: EndRecurranceALL, label: t('allButtons') }], testId: 'all-buttons' },
                                { options: buttonsOptions, testId: 'available-buttons' },
                                { options: [{ value: EndRecurranceNONE, label: t('none') }], testId: 'no-buttons' },
                            ]}
                            isDisabled={disabled}
                            className={classes.select}
                            menuPlacement="auto"
                            components={{ Group: CustomGroup }}
                            testKey={`end-recurrence-buttons`}
                            error={invalid && t('error')}
                            multiValueGroupLabel={t('itemsSelected')}
                        />
                    );
                }}
                name="endReoccurrence"
                defaultValue={selectedValues}
            />
        </div>
    );
};
